import {
  FONTE_DE_TEXTO_PADRAO_ID,
  MAX_CAMADAS_POR_CELULA,
  MAX_CARACTERES_POR_LINHA,
  MAX_LINHAS_DO_TEXTO,
  escolherArquivoDaFonte,
  fonteDeTexto,
  limitarCamada,
  linhasDoTexto,
  novoIdDeCamada,
  type Camada,
  type CamadaDeImagem,
  type CamadaDeTexto,
  type FigurinhaDaBiblioteca,
  type FonteDeTexto,
  type Tamanho,
} from "@foto/shared";

/**
 * As contas do editor de camadas que não são geometria: o que uma camada nova leva de partida, como se
 * chama cada uma na lista, o que cada tecla faz. Tudo puro, para testar sem navegador — o Vitest do web
 * só roda módulo puro.
 *
 * A geometria em si (caixa, clique, arrastar, esticar, girar) NÃO está aqui: mora em `@foto/shared`, e a tela
 * nunca a reimplementa — a prévia e o servidor têm de concordar.
 */

export const TEXTO_INICIAL = "Seu texto";

/** Quanto da altura da célula uma figurinha recém-colocada pode ocupar: o resto é foto. */
const ALTURA_MAXIMA_DA_NOVA = 50;

export const cabeOutraCamada = (camadas: readonly Camada[]): boolean =>
  camadas.length < MAX_CAMADAS_POR_CELULA;

// ─── Camadas novas ────────────────────────────────────────────────────────────

/**
 * A largura (% da célula) com que a imagem entra: a figurinha ocupa ~40%, o logo ~30% (é uma assinatura, e não
 * o assunto da foto) e a arte de letras ~60% (é larga por natureza). Uma imagem muito alta é encolhida até
 * ocupar no máximo metade da altura da célula, para não nascer cobrindo a foto inteira.
 */
export function larguraInicialDaImagem(
  figurinha: Pick<FigurinhaDaBiblioteca, "tipo" | "largura" | "altura">,
  celula: Tamanho,
): number {
  const base = figurinha.tipo === "logo" ? 30 : figurinha.tipo === "texto" ? 60 : 40;
  if (!(figurinha.largura > 0) || !(figurinha.altura > 0)) return base;

  // Altura da imagem em % da altura da célula, quando a largura é `base`.
  const alturaEmPorcento =
    ((base / 100) * celula.largura * (figurinha.altura / figurinha.largura) * 100) / celula.altura;
  const largura =
    alturaEmPorcento > ALTURA_MAXIMA_DA_NOVA
      ? (base * ALTURA_MAXIMA_DA_NOVA) / alturaEmPorcento
      : base;
  return Math.round(Math.max(5, largura) * 100) / 100;
}

/**
 * Onde a camada nova entra: no centro. Se já há uma camada praticamente no mesmo ponto, desce e vai para a
 * direita um pouco — duas camadas exatamente empilhadas parecem uma só, e o operador acha que a segunda não
 * entrou.
 */
export function posicaoDaNovaCamada(camadas: readonly Camada[]): { x: number; y: number } {
  let ponto = { x: 50, y: 50 };
  for (let tentativa = 0; tentativa < 6; tentativa++) {
    const ocupado = camadas.some(
      (c) => Math.abs(c.x - ponto.x) < 1.5 && Math.abs(c.y - ponto.y) < 1.5,
    );
    if (!ocupado) break;
    ponto = { x: ponto.x + 4, y: ponto.y + 4 };
  }
  return ponto;
}

export function camadaDeImagemNova(
  figurinha: Pick<FigurinhaDaBiblioteca, "id" | "tipo" | "largura" | "altura">,
  camadas: readonly Camada[],
  celula: Tamanho,
): CamadaDeImagem {
  return limitarCamada({
    id: novoIdDeCamada(camadas.map((c) => c.id)),
    tipo: "imagem" as const,
    figurinhaId: figurinha.id,
    ...posicaoDaNovaCamada(camadas),
    rotacao: 0,
    largura: larguraInicialDaImagem(figurinha, celula),
  });
}

/**
 * O texto de partida: branco com contorno escuro, que lê sobre qualquer foto, na fonte e no peso padrão do
 * pacote. O corpo 8 (% da largura da célula) dá umas poucas palavras numa foto 3 × 4.
 */
export function camadaDeTextoNova(camadas: readonly Camada[]): CamadaDeTexto {
  const fonte = fonteDeTexto(FONTE_DE_TEXTO_PADRAO_ID);
  return limitarCamada({
    id: novoIdDeCamada(camadas.map((c) => c.id)),
    tipo: "texto" as const,
    ...posicaoDaNovaCamada(camadas),
    rotacao: 0,
    texto: TEXTO_INICIAL,
    fonteId: FONTE_DE_TEXTO_PADRAO_ID,
    corpo: 8,
    cor: "#ffffff",
    contorno: { cor: "#1a1a1a", espessura: 0.08 },
    alinhamento: "centro" as const,
    peso: fonte?.pesoPadrao ?? 700,
    italico: false,
    entrelinha: 1.2,
  });
}

// ─── Nomes ────────────────────────────────────────────────────────────────────

const LIMITE_DO_NOME = 28;

/**
 * Como a camada aparece na lista: o nome da figurinha, ou as primeiras palavras do texto. `figurinha`
 * indefinida é uma imagem que ainda carrega (ou que não existe mais).
 */
export function nomeDaCamada(
  camada: Camada,
  figurinha: Pick<FigurinhaDaBiblioteca, "nome"> | undefined,
): string {
  if (camada.tipo === "imagem") return figurinha?.nome ?? "Imagem";
  const primeira = linhasDoTexto(camada.texto)[0] ?? "";
  if (primeira === "") return "Texto em branco";
  return primeira.length > LIMITE_DO_NOME ? `${primeira.slice(0, LIMITE_DO_NOME - 1)}…` : primeira;
}

// ─── Texto digitado ───────────────────────────────────────────────────────────

/** Corta no limite sem partir um par substituto (emoji): metade de um emoji é um caractere inválido. */
function cortarLinha(linha: string, max: number): string {
  if (linha.length <= max) return linha;
  const corte = linha.slice(0, max);
  const ultimo = corte.charCodeAt(corte.length - 1);
  return ultimo >= 0xd800 && ultimo <= 0xdbff ? corte.slice(0, -1) : corte;
}

/**
 * O que o campo de texto entrega, dentro do que o servidor aceita: no máximo `MAX_LINHAS_DO_TEXTO` linhas de
 * `MAX_CARACTERES_POR_LINHA` letras. `cortado` diz se algo ficou de fora, para a tela avisar em vez de comer
 * letras em silêncio.
 */
export function limitarTextoDigitado(texto: string): { texto: string; cortado: boolean } {
  const linhas = texto.replace(/\r\n?/g, "\n").split("\n");
  const aceitas = linhas
    .slice(0, MAX_LINHAS_DO_TEXTO)
    .map((l) => cortarLinha(l, MAX_CARACTERES_POR_LINHA));
  const saida = aceitas.join("\n");
  return { texto: saida, cortado: saida !== texto.replace(/\r\n?/g, "\n") };
}

/** Os números do contador do campo: quantas linhas e quantas letras tem a maior. */
export function medidasDoTexto(texto: string): { linhas: number; maiorLinha: number } {
  const linhas = texto.replace(/\r\n?/g, "\n").split("\n");
  return { linhas: linhas.length, maiorLinha: Math.max(...linhas.map((l) => l.length)) };
}

export const textoEmBranco = (camada: Camada): boolean =>
  camada.tipo === "texto" && camada.texto.trim() === "";

/**
 * As camadas que o servidor aceita: o texto em branco é recusado pelo schema (`min(1)` e "não pode ficar em
 * branco"), e uma camada assim não tem o que desenhar. Sai daqui, e não é mandada.
 */
export const semTextosEmBranco = (camadas: readonly Camada[]): Camada[] =>
  camadas.filter((c) => !textoEmBranco(c));

// ─── Tamanho aproximado do bloco de texto ─────────────────────────────────────

/**
 * Uma estimativa do bloco de texto, para o primeiro quadro (antes de a tela medir o elemento de verdade, e
 * enquanto a fonte não chegou): sem ela a caixa de seleção nasceria com tamanho zero. A medida real vem do
 * DOM e a substitui.
 */
export function estimarBlocoDeTexto(camada: CamadaDeTexto, celula: Tamanho): Tamanho {
  const corpo = (camada.corpo / 100) * celula.largura;
  const linhas = linhasDoTexto(camada.texto);
  const maior = Math.max(1, ...linhas.map((l) => l.length));
  return {
    largura: maior * corpo * 0.55,
    altura: Math.max(1, linhas.length) * corpo * camada.entrelinha,
  };
}

// ─── Mudar a camada ───────────────────────────────────────────────────────────

/** Multiplica o tamanho da camada (a largura da imagem, o corpo do texto), com o centro parado. */
export function escalarCamada<C extends Camada>(camada: C, fator: number): C {
  return limitarCamada(
    camada.tipo === "imagem"
      ? { ...camada, largura: camada.largura * fator }
      : { ...camada, corpo: camada.corpo * fator },
  );
}

/** O tamanho da camada: `largura` (% da célula) na imagem, `corpo` (% da largura) no texto. */
export const tamanhoDaCamada = (camada: Camada): number =>
  camada.tipo === "imagem" ? camada.largura : camada.corpo;

export function definirTamanhoDaCamada<C extends Camada>(camada: C, valor: number): C {
  return limitarCamada(
    camada.tipo === "imagem" ? { ...camada, largura: valor } : { ...camada, corpo: valor },
  );
}

export function girarCamada<C extends Camada>(camada: C, graus: number): C {
  return limitarCamada({ ...camada, rotacao: camada.rotacao + graus });
}

export function definirRotacao<C extends Camada>(camada: C, graus: number): C {
  return limitarCamada({ ...camada, rotacao: graus });
}

/** Desloca a camada, em % da célula (a tecla de seta). O centro continua dentro da célula. */
export function deslocarCamada<C extends Camada>(camada: C, dx: number, dy: number): C {
  return limitarCamada({ ...camada, x: camada.x + dx, y: camada.y + dy });
}

// ─── A fonte e o peso ─────────────────────────────────────────────────────────

/**
 * O peso que de fato vale para a fonte e o itálico pedidos: o mesmo caminho do servidor
 * (`escolherArquivoDaFonte`). Fonte de peso único sempre cai no seu peso; a estática, no mais próximo; e o
 * itálico de Poppins só existe no 400 e no 700.
 */
export const pesoEfetivo = (fonte: FonteDeTexto, peso: number, italico: boolean): number =>
  escolherArquivoDaFonte(fonte, peso, italico).peso;

const NOME_DO_PESO: Record<number, string> = {
  100: "Fino",
  200: "Extraleve",
  300: "Leve",
  400: "Normal",
  500: "Médio",
  600: "Seminegrito",
  700: "Negrito",
  800: "Extranegrito",
  900: "Preto",
};

export const rotuloDoPeso = (peso: number): string => `${NOME_DO_PESO[peso] ?? "Peso"} (${peso})`;

/**
 * Troca a fonte da camada de texto. O peso volta ao padrão da fonte nova (o da fonte antiga pode nem existir
 * nela) e o itálico some se a nova não tem — mesmo que a tela escondesse o controle, o pedido não levaria um
 * itálico que o servidor ignoraria.
 */
export function trocarFonteDoTexto(camada: CamadaDeTexto, fonte: FonteDeTexto): CamadaDeTexto {
  const italico = camada.italico && fonte.arquivos.some((a) => a.italico);
  return {
    ...camada,
    fonteId: fonte.id,
    italico,
    peso: pesoEfetivo(fonte, fonte.pesoPadrao, italico),
  };
}

/** Liga ou desliga o itálico; o peso acompanha o que existe naquele estilo. */
export function definirItalico(camada: CamadaDeTexto, italico: boolean): CamadaDeTexto {
  const fonte = fonteDeTexto(camada.fonteId);
  if (!fonte) return { ...camada, italico };
  return { ...camada, italico, peso: pesoEfetivo(fonte, camada.peso, italico) };
}

// ─── O teclado ────────────────────────────────────────────────────────────────

export type ComandoDeTeclado =
  | { tipo: "deslocar"; dx: number; dy: number }
  | { tipo: "escalar"; fator: number }
  | { tipo: "apagar" }
  | { tipo: "duplicar" }
  | { tipo: "desfazer" }
  | { tipo: "refazer" };

/** O passo da seta, em % da célula; com Shift, o maior. */
export const PASSO_DA_SETA = 0.5;
export const PASSO_DA_SETA_GRANDE = 5;
/** O passo do + e do −: 5% de cada vez. */
export const FATOR_DO_MAIS_E_MENOS = 1.05;

export interface TeclaApertada {
  key: string;
  shiftKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
}

/**
 * O que a tecla faz com a camada selecionada: setas movem (Shift, passo maior), + e − escalam, Delete apaga,
 * Ctrl/Cmd+D duplica, Ctrl/Cmd+Z desfaz e Ctrl/Cmd+Shift+Z (ou Ctrl+Y) refaz. `null` é "não é comigo": o
 * navegador segue com a tecla.
 */
export function comandoDoTeclado(e: TeclaApertada): ComandoDeTeclado | null {
  const comando = e.ctrlKey || e.metaKey;
  const tecla = e.key.length === 1 ? e.key.toLowerCase() : e.key;

  if (comando && !e.altKey) {
    if (tecla === "d") return { tipo: "duplicar" };
    if (tecla === "z") return e.shiftKey ? { tipo: "refazer" } : { tipo: "desfazer" };
    if (tecla === "y") return { tipo: "refazer" };
    return null;
  }
  if (comando || e.altKey) return null;

  const passo = e.shiftKey ? PASSO_DA_SETA_GRANDE : PASSO_DA_SETA;
  switch (e.key) {
    case "ArrowLeft":
      return { tipo: "deslocar", dx: -passo, dy: 0 };
    case "ArrowRight":
      return { tipo: "deslocar", dx: passo, dy: 0 };
    case "ArrowUp":
      return { tipo: "deslocar", dx: 0, dy: -passo };
    case "ArrowDown":
      return { tipo: "deslocar", dx: 0, dy: passo };
    case "Delete":
    case "Backspace":
      return { tipo: "apagar" };
    // O "+" do teclado brasileiro é Shift+=: por isso o Shift não muda o passo do + e do −.
    case "+":
    case "=":
      return { tipo: "escalar", fator: FATOR_DO_MAIS_E_MENOS };
    case "-":
    case "_":
      return { tipo: "escalar", fator: 1 / FATOR_DO_MAIS_E_MENOS };
    default:
      return null;
  }
}
